// Planilla row filters: which operators each column type offers, and whether
// a unit passes a filter. Pure functions -- no React -- so the rules can be
// checked on their own (node, no browser).
//
// A filter is { campo, op, valor }. `campo` is a Planilla column id, or one of
// the fixed fields in CAMPOS_FIJOS. Several filters combine with AND.

// Fixed unit fields that filter like text columns.
export const CAMPOS_FIJOS = [
  { id: 'codigo', tipo: 'texto' },
]

export const OPERADORES = {
  texto:   ['is', 'is_not', 'contains', 'not_contains', 'empty', 'not_empty'],
  persona: ['contains', 'not_contains', 'empty', 'not_empty'],
  estado:  ['is', 'is_not', 'empty', 'not_empty'],
  numero:  ['is', 'is_not', 'gt', 'lt', 'gte', 'lte', 'empty', 'not_empty'],
  fecha:   ['is', 'before', 'after', 'empty', 'not_empty'],
  archivo: ['not_empty', 'empty'],
}

// Operators that take no value.
export const SIN_VALOR = new Set(['empty', 'not_empty'])

// In a list filter (op 'in'), the token that stands for the empty cells.
export const VACIO = '__vacio__'

const blank = (v) => v == null || String(v).trim() === ''
const norm = (v) => String(v ?? '').trim().toLowerCase()
const num = (v) => {
  if (blank(v)) return NaN
  return Number(String(v).trim().replace(',', '.'))
}

// A filter still being written (no value yet) filters nothing, instead of
// hiding every row while someone types.
export function filtroCompleto(f) {
  return !!f.campo && !!f.op && (SIN_VALOR.has(f.op) || !blank(f.valor))
}

// The value a filter compares against for one unit: the cell's text, or for
// an archivo column the number of files (so "empty" means "no files").
// People cells store ids; filters compare the people's names, set once the
// register is loaded (components/PersonaPicker usePersonas).
let nombresPersonas = {}
export function setNombresPersonas(mapa) { nombresPersonas = mapa || {} }

export function valorDe(unidad, columna) {
  if (columna.fijo) return unidad[columna.id] ?? null
  if (columna.tipo === 'archivo') return (unidad.archivos?.[columna.nombre] || []).length || null
  if (columna.tipo === 'persona') {
    const v = unidad.atributos?.[columna.nombre]
    let ids = []
    try { ids = v ? [].concat(JSON.parse(v)) : [] } catch { ids = [] }
    return ids.map(id => nombresPersonas[id] || `#${id}`).join(', ') || null
  }
  return unidad.atributos?.[columna.nombre] ?? null
}

export function cumple(unidad, columna, f) {
  if (!filtroCompleto(f)) return true
  const v = valorDe(unidad, columna)
  const tipo = columna.tipo
  switch (f.op) {
    case 'empty':     return blank(v)
    case 'not_empty': return !blank(v)
    case 'is':
      if (blank(v)) return false
      if (tipo === 'numero') return num(v) === num(f.valor)
      if (tipo === 'estado' || tipo === 'fecha') return String(v) === String(f.valor)
      return norm(v) === norm(f.valor)
    // "is not X" keeps the empty cells: they aren't X either.
    case 'is_not':
      return !cumple(unidad, columna, { ...f, op: 'is' })
    case 'contains':     return !blank(v) && norm(v).includes(norm(f.valor))
    case 'not_contains': return blank(v) || !norm(v).includes(norm(f.valor))
    // Comparisons: an empty or non-numeric cell never passes.
    case 'gt':  return num(v) > num(f.valor)
    case 'lt':  return num(v) < num(f.valor)
    case 'gte': return num(v) >= num(f.valor)
    case 'lte': return num(v) <= num(f.valor)
    // ISO yyyy-mm-dd compares correctly as text.
    case 'before': return !blank(v) && String(v) < String(f.valor)
    case 'after':  return !blank(v) && String(v) > String(f.valor)
    // Header filters of a column (the Partes board): a year or a month of a date ("2026", "2026-04"), a range
    // ("from|to", either end may be empty) and a list of values ("a|b|c"; EMPTY in it keeps the empty cells).
    case 'year':    return !blank(v) && String(v).slice(0, 4) === String(f.valor)
    case 'month':   return !blank(v) && String(v).slice(0, 7) === String(f.valor)
    case 'between': {
      if (blank(v)) return false
      const [desde, hasta] = String(f.valor).split('|')
      return (!desde || String(v) >= desde) && (!hasta || String(v) <= hasta)
    }
    case 'nbetween': {                                             // numeric range "min|max", either end may be empty
      const n = num(v)
      if (Number.isNaN(n)) return false
      const [min, max] = String(f.valor).split('|')
      return (blank(min) || n >= num(min)) && (blank(max) || n <= num(max))
    }
    case 'in': {
      const lista = String(f.valor).split('|')
      return blank(v) ? lista.includes(VACIO) : lista.includes(String(v))
    }
    default: return true
  }
}

// `columnas`: every filterable column, fixed fields included (see
// columnasFiltrables). Filters whose column no longer exists are ignored.
export function aplicarFiltros(unidades, filtros, columnas) {
  const activos = filtros.filter(filtroCompleto)
    .map(f => ({ f, col: columnas.find(c => String(c.id) === String(f.campo)) }))
    .filter(x => x.col)
  if (!activos.length) return unidades
  return unidades.filter(u => activos.every(({ f, col }) => cumple(u, col, f)))
}

// Fixed fields + the Planilla's columns, in one list the filter UI offers.
// `extras`: more fixed fields read straight off each record (e.g. the
// computed "desviaciones" while comparing against the bus ideal). `base`:
// the record's own fields -- a unit's by default; the Partes board passes
// its own.
export function columnasFiltrables(columnas, etiquetasFijas = {}, extras = [], base = CAMPOS_FIJOS) {
  return [
    ...[...base, ...extras].map(c => ({ ...c, fijo: true, nombre: etiquetasFijas[c.id] || c.id })),
    ...columnas,
  ]
}
